import { Link, NavLink } from 'react-router-dom';
import { ExternalLink, Store } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminSession } from './session';
import { ADMIN_NAV } from './nav';

/** Nav filtered by the actor's permissions AND the tenant's enabled modules. Empty sections disappear. */
export function useVisibleAdminNav() {
  const actor = useAdminSession((s) => s.actor);
  if (!actor) return [];
  return ADMIN_NAV
    .map((s) => ({ ...s, items: s.items.filter((i) => actor.permissions.includes(i.permission) && (!i.feature || actor.tenant.features[i.feature])) }))
    .filter((s) => s.items.length > 0);
}

export function StoreIdentity({ collapsed }: { collapsed?: boolean }) {
  const t = useAdminSession((s) => s.actor?.tenant);
  return (
    <Link to="/admin" className="flex min-w-0 items-center gap-3" aria-label="Admin home">
      {t?.logoUrl ? <img src={t.logoUrl} alt="" className="size-9 shrink-0 rounded-xl object-contain" /> : <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-glow"><Store className="size-5" /></span>}
      {!collapsed && (
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-sm font-extrabold">{t?.name ?? 'Store'}</span>
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">MCE Admin</span>
        </span>
      )}
    </Link>
  );
}

export function SidebarNav({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const sections = useVisibleAdminNav();
  return (
    <nav aria-label="Admin" className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {sections.map((s) => (
        <div key={s.title}>
          {!collapsed ? <p className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{s.title}</p> : <div className="mx-3 mb-2 h-px bg-border" aria-hidden />}
          <ul className="space-y-0.5">
            {s.items.map((i) => (
              <li key={i.to}>
                <NavLink
                  to={i.to} end={i.end} onClick={onNavigate} title={collapsed ? i.label : undefined}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors',
                    collapsed && 'justify-center px-0',
                    isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <i.icon className="size-[18px] shrink-0" aria-hidden />
                  <span className={cn(collapsed && 'sr-only')}>{i.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function AdminSidebar({ collapsed }: { collapsed: boolean }) {
  return (
    <aside className={cn('fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex', collapsed ? 'w-[4.5rem]' : 'w-64')}>
      <div className={cn('flex h-16 items-center border-b border-border', collapsed ? 'justify-center px-2' : 'px-5')}><StoreIdentity collapsed={collapsed} /></div>
      <SidebarNav collapsed={collapsed} />
      <div className="border-t border-border p-3">
        <Link to="/" target="_blank" className={cn('flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground', collapsed && 'justify-center px-0')} title="Open storefront">
          <ExternalLink className="size-[18px]" /><span className={cn(collapsed && 'sr-only')}>View storefront</span>
        </Link>
      </div>
    </aside>
  );
}
